import React, { useMemo, useEffect, useRef, useCallback } from "react";
import L from "leaflet";
import styled from "styled-components";
import CITY_DATA from "../../../../util/cityData";

// Import Leaflet CSS
import "leaflet/dist/leaflet.css";

// Fix for default marker icons (if needed in future)
try {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
    iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  });
} catch (e) {
  // Ignore if icons already set
}

const MapWrapper = styled.div`
  width: 100%;
  height: 500px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #ddd;

  .leaflet-container {
    height: 100%;
    width: 100%;
  }

  @media (max-width: 768px) {
    height: 300px;
  }
`;

const MapDiv = styled.div`
  width: 100%;
  height: 100%;
  min-height: 500px;
  background: #f0f0f0;

  @media (max-width: 768px) {
    min-height: 300px;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

/**
 * Calculate distance between two lat/lng points in meters (Haversine formula)
 */
const getDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371000; // Earth's radius in meters
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Convert meters to pixels at a given zoom level
 */
const metersToPixels = (meters, zoom, lat) => {
  const earthCircumference = 40075017; // meters
  const latRad = (lat * Math.PI) / 180;
  const metersPerPixel =
    (earthCircumference * Math.cos(latRad)) / Math.pow(2, zoom + 8);
  return meters / metersPerPixel;
};

/**
 * Cluster overlapping points
 * @param {Array} points - Array of {lat, lng, count} objects
 * @param {number} zoom - Current zoom level
 * @returns {Array} Clustered points
 */
const clusterPoints = (points, zoom) => {
  if (!points || points.length === 0) return [];

  // At higher zoom levels, be more selective about clustering
  // Zoom 10: cluster if circles touch (100% threshold)
  // Zoom 12: cluster if 70% overlap
  // Zoom 14: cluster if 50% overlap  
  // Zoom 16+: cluster if 30% overlap (very close only)
  const zoomFactor = Math.max(0.2, Math.min(1.0, 1.0 - (zoom - 10) * 0.15));
  
  const maxCount = Math.max(...points.map((p) => p.count || 1));
  const clusters = [];
  const processed = new Set();

  points.forEach((point, index) => {
    if (processed.has(index)) return;

    const lat = point.lat || (point._id && point._id.lat);
    const lng = point.lng || (point._id && point._id.lng);
    const count = point.count || 1;

    if (!lat || !lng || isNaN(lat) || isNaN(lng)) return;

    // Calculate radius for this point in meters
    const normalizedCount = count / maxCount;
    const radiusPixels = 5 + normalizedCount * 15;
    // Convert pixels to meters at this zoom level and latitude
    const metersPerPixel = (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);
    const radiusMeters = radiusPixels * metersPerPixel;

    // Find all points that overlap with this one
    const cluster = {
      points: [{ lat, lng, count, index }],
      totalCount: count,
      centerLat: lat,
      centerLng: lng,
    };

    processed.add(index);

    // Check other points for overlap
    points.forEach((otherPoint, otherIndex) => {
      if (processed.has(otherIndex) || index === otherIndex) return;

      const otherLat = otherPoint.lat || (otherPoint._id && otherPoint._id.lat);
      const otherLng = otherPoint.lng || (otherPoint._id && otherPoint._id.lng);
      const otherCount = otherPoint.count || 1;

      if (!otherLat || !otherLng || isNaN(otherLat) || isNaN(otherLng)) return;

      // Calculate distance between points
      const distance = getDistance(lat, lng, otherLat, otherLng);

      // Calculate radius for other point
      const otherNormalizedCount = otherCount / maxCount;
      const otherRadiusPixels = 5 + otherNormalizedCount * 15;
      const otherMetersPerPixel = (156543.03392 * Math.cos((otherLat * Math.PI) / 180)) / Math.pow(2, zoom);
      const otherRadiusMeters = otherRadiusPixels * otherMetersPerPixel;

      // Apply zoom factor: at higher zoom, require more overlap to cluster
      // This makes clusters split apart as you zoom in
      const sumOfRadii = radiusMeters + otherRadiusMeters;
      const overlapThreshold = sumOfRadii * zoomFactor;
      
      // Only cluster if circles significantly overlap (based on zoom level)
      if (distance < overlapThreshold) {
        cluster.points.push({ lat: otherLat, lng: otherLng, count: otherCount, index: otherIndex });
        cluster.totalCount += otherCount;
        // Update center to weighted average
        const totalWeight = cluster.points.reduce((sum, p) => sum + p.count, 0);
        cluster.centerLat = cluster.points.reduce((sum, p) => sum + p.lat * p.count, 0) / totalWeight;
        cluster.centerLng = cluster.points.reduce((sum, p) => sum + p.lng * p.count, 0) / totalWeight;
        processed.add(otherIndex);
      }
    });

    clusters.push(cluster);
  });

  return clusters.map((cluster) => ({
    lat: cluster.centerLat,
    lng: cluster.centerLng,
    count: cluster.totalCount,
    pointCount: cluster.points.length, // Number of points in this cluster
  }));
};

/**
 * Convert a value (0-1) to a heatmap color (blue -> green -> yellow -> red)
 * @param {number} value - Normalized value between 0 and 1
 * @returns {string} Hex color code
 */
const getHeatmapColor = (value) => {
  // Clamp value between 0 and 1
  value = Math.max(0, Math.min(1, value));
  
  // Color gradient: blue (0) -> cyan -> green -> yellow -> orange -> red (1)
  if (value < 0.2) {
    // Blue to cyan
    const t = value / 0.2;
    const r = Math.floor(0 + t * 0);
    const g = Math.floor(100 + t * 155);
    const b = Math.floor(200 + t * 55);
    return `rgb(${r}, ${g}, ${b})`;
  } else if (value < 0.4) {
    // Cyan to green
    const t = (value - 0.2) / 0.2;
    const r = Math.floor(0 + t * 0);
    const g = Math.floor(255 + t * 0);
    const b = Math.floor(255 - t * 200);
    return `rgb(${r}, ${g}, ${b})`;
  } else if (value < 0.6) {
    // Green to yellow
    const t = (value - 0.4) / 0.2;
    const r = Math.floor(0 + t * 255);
    const g = Math.floor(255 - t * 0);
    const b = Math.floor(55 - t * 55);
    return `rgb(${r}, ${g}, ${b})`;
  } else if (value < 0.8) {
    // Yellow to orange
    const t = (value - 0.6) / 0.2;
    const r = Math.floor(255);
    const g = Math.floor(255 - t * 100);
    const b = Math.floor(0);
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // Orange to red
    const t = (value - 0.8) / 0.2;
    const r = Math.floor(255);
    const g = Math.floor(155 - t * 155);
    const b = Math.floor(0);
    return `rgb(${r}, ${g}, ${b})`;
  }
};

const GeographicHeatmap = ({ data, loading, selectedCity, onCitySelect }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const heatmapLayerRef = useRef(null);
  const [currentZoom, setCurrentZoom] = React.useState(10);
  const [isMapReady, setIsMapReady] = React.useState(false);

  const center = useMemo(() => {
    if (selectedCity) {
      return [selectedCity.lat, selectedCity.lng];
    }
    if (data && data.length > 0) {
      const avgLat = data.reduce((sum, point) => {
        const lat = point.lat || (point._id && point._id.lat) || 0;
        return sum + lat;
      }, 0) / data.length;
      const avgLng = data.reduce((sum, point) => {
        const lng = point.lng || (point._id && point._id.lng) || 0;
        return sum + lng;
      }, 0) / data.length;
      return [avgLat, avgLng];
    }
    return [42.3601, -71.0589]; // Default to Boston
  }, [data, selectedCity]);

  const zoom = selectedCity ? 12 : 10;

  // Initialize map
  useEffect(function initializeLeafletMap() {
    // Check if Leaflet is available
    if (typeof L === "undefined") {
      console.error("Leaflet is not loaded. Make sure leaflet package is installed.");
      return;
    }

    if (mapInstanceRef.current) {
      return; // Already initialized
    }

    // Initialize map with retry logic
    const initMap = () => {
      if (!mapRef.current) {
        console.log("Map ref not ready, retrying...");
        return false;
      }
      
      if (mapInstanceRef.current) {
        return true; // Already initialized
      }

      try {
        console.log("Initializing Leaflet map at", center);
        
        const map = L.map(mapRef.current, {
          zoomControl: true,
        }).setView(center, zoom);

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19,
        }).addTo(map);

        mapInstanceRef.current = map;
        console.log("Map initialized successfully");
        
        // Set initial zoom state
        setCurrentZoom(map.getZoom());
        setIsMapReady(true);

        // Invalidate size after a delay to ensure container is sized
        setTimeout(() => {
          if (mapInstanceRef.current) {
            mapInstanceRef.current.invalidateSize();
            console.log("Map size invalidated");
          }
        }, 300);
        
        return true;
      } catch (error) {
        console.error("Error initializing map:", error);
        return false;
      }
    };

    // Try immediately
    if (!initMap()) {
      // Retry with delays if not ready
      const timer1 = setTimeout(() => {
        if (!mapInstanceRef.current) initMap();
      }, 100);
      
      const timer2 = setTimeout(() => {
        if (!mapInstanceRef.current) initMap();
      }, 500);
      
      const timer3 = setTimeout(() => {
        if (!mapInstanceRef.current) initMap();
      }, 1000);

      // Cleanup on unmount
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
        if (mapInstanceRef.current) {
          try {
            mapInstanceRef.current.remove();
          } catch (e) {
            console.error("Error removing map:", e);
          }
          mapInstanceRef.current = null;
        }
      };
    }

    // Cleanup on unmount
    return () => {
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.remove();
        } catch (e) {
          console.error("Error removing map:", e);
        }
        mapInstanceRef.current = null;
      }
    };
  }, []); // Only run once on mount

  // Update map center when data changes
  useEffect(function updateMapCenterWhenDataChanges() {
    if (mapInstanceRef.current && center) {
      mapInstanceRef.current.setView(center, zoom);
    }
  }, [center, zoom]);

  // Track zoom changes - set up listener when map is ready
  useEffect(function setupZoomChangeListener() {
    if (!isMapReady || !mapInstanceRef.current) return;

    const updateZoom = () => {
      if (mapInstanceRef.current) {
        const newZoom = mapInstanceRef.current.getZoom();
        console.log("Zoom changed to:", newZoom);
        setCurrentZoom(newZoom);
      }
    };

    // Set up zoom event listeners
    mapInstanceRef.current.on("zoomend", updateZoom);
    mapInstanceRef.current.on("zoom", updateZoom); // Also listen during zoom animation
    
    // Set initial zoom if not already set
    const currentMapZoom = mapInstanceRef.current.getZoom();
    if (currentMapZoom !== currentZoom) {
      setCurrentZoom(currentMapZoom);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.off("zoomend", updateZoom);
        mapInstanceRef.current.off("zoom", updateZoom);
      }
    };
  }, [isMapReady]); // Re-run when map becomes ready

  // Map click handler — select nearest city
  const handleMapClick = useCallback(
    (e) => {
      if (!onCitySelect) return;
      const { lat, lng } = e.latlng;
      let nearest = null;
      let minDist = Infinity;
      CITY_DATA.forEach((city) => {
        const dist = getDistance(lat, lng, city.lat, city.lng);
        if (dist < minDist) {
          minDist = dist;
          nearest = city;
        }
      });
      // Only select if within 50km
      if (nearest && minDist < 50000) {
        onCitySelect(nearest);
      }
    },
    [onCitySelect]
  );

  useEffect(function setupMapClickHandler() {
    if (!isMapReady || !mapInstanceRef.current) return;
    mapInstanceRef.current.on("click", handleMapClick);
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.off("click", handleMapClick);
      }
    };
  }, [isMapReady, handleMapClick]);

  // Update heatmap layer when data or zoom changes
  useEffect(function updateHeatmapLayerWhenDataOrZoomChanges() {
    // Wait for map to be ready
    if (!isMapReady || !mapInstanceRef.current) {
      console.log("Map not ready for heatmap data, will retry...");
      return;
    }

    // If no data, just clear the heatmap layer but keep the map
    if (!data || data.length === 0) {
      console.log("No heatmap data available, clearing heatmap layer");
      if (heatmapLayerRef.current) {
        mapInstanceRef.current.removeLayer(heatmapLayerRef.current);
        heatmapLayerRef.current = null;
      }
      return;
    }

    console.log(`Updating heatmap with ${data.length} data points`, data.slice(0, 3));

    // Remove existing heatmap layer if it exists
    if (heatmapLayerRef.current) {
      mapInstanceRef.current.removeLayer(heatmapLayerRef.current);
      heatmapLayerRef.current = null;
    }

    // Prepare data points
    const points = data.map((point) => {
      const lat = point.lat || (point._id && point._id.lat);
      const lng = point.lng || (point._id && point._id.lng);
      const count = point.count || 1;
      return { lat, lng, count };
    }).filter((p) => p.lat && p.lng && !isNaN(p.lat) && !isNaN(p.lng));

    if (points.length === 0) {
      console.log("No valid points to display");
      return;
    }

    // Get current zoom from map if available (more reliable than state)
    const actualZoom = mapInstanceRef.current ? mapInstanceRef.current.getZoom() : currentZoom;
    
    // Cluster overlapping points based on current zoom
    const clusteredPoints = clusterPoints(points, actualZoom);
    console.log(`Clustered ${points.length} points into ${clusteredPoints.length} clusters at zoom ${actualZoom} (state: ${currentZoom})`);

    // Find max count for normalization (use clustered counts)
    const maxCount = Math.max(...clusteredPoints.map((p) => p.count || 1));

    // Create a layer group for heatmap points
    const heatmapLayer = L.layerGroup();

    let validPoints = 0;
    // Add circle markers for clustered points
    clusteredPoints.forEach((cluster) => {
      const normalizedCount = cluster.count / maxCount;
      const radius = 5 + normalizedCount * 15; // Radius between 5 and 20
      const opacity = 0.4 + normalizedCount * 0.4; // Opacity between 0.4 and 0.8
      const color = getHeatmapColor(normalizedCount);

      const tooltipText = cluster.pointCount > 1
        ? `Count: ${cluster.count} (${cluster.pointCount} locations)`
        : `Count: ${cluster.count}`;

      const circle = L.circleMarker([cluster.lat, cluster.lng], {
        radius: radius,
        fillColor: color,
        color: color,
        weight: 1,
        opacity: Math.min(0.9, opacity),
        fillOpacity: opacity,
      });

      circle.bindTooltip(tooltipText, {
        permanent: false,
        direction: "top",
      });

      heatmapLayer.addLayer(circle);
      validPoints++;
    });

    console.log(`Added ${validPoints} valid points to heatmap`);

    if (validPoints > 0) {
      heatmapLayer.addTo(mapInstanceRef.current);
      heatmapLayerRef.current = heatmapLayer;
    } else {
      console.warn("No valid points to display on heatmap");
    }

    // Cleanup on unmount or data/zoom change
    return () => {
      if (heatmapLayerRef.current && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(heatmapLayerRef.current);
        heatmapLayerRef.current = null;
      }
    };
  }, [data, currentZoom, isMapReady]); // Re-cluster when zoom or data changes

  return (
    <MapWrapper>
      <MapDiv ref={mapRef} />
      {loading && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "rgba(255, 255, 255, 0.95)",
            padding: "20px",
            borderRadius: "8px",
            zIndex: 1000,
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          }}
        >
          <LoadingMessage>Loading heatmap data...</LoadingMessage>
        </div>
      )}
      {!loading && (!data || data.length === 0) && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "rgba(255, 255, 255, 0.9)",
            padding: "20px",
            borderRadius: "8px",
            zIndex: 1000,
          }}
        >
          <LoadingMessage>No search data available for the selected time period.</LoadingMessage>
        </div>
      )}
    </MapWrapper>
  );
};

export default GeographicHeatmap;

